/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation } from "@tanstack/react-query";
import { useContext, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  createProductQuery,
  deleteProductQuery,
  getProductDetailQuery,
  getProductListQuery,
  updateProductQuery,
  listProductItemsByProductQuery,
  getLowStocAlertsQuery
} from "../../Graphql/querries/productQuery";
import {
  setProductDetail,
  setProductList,
  setProductListPagination,
  incrementProductListRefreshTrigger,
  setLowStockAlerts
} from "../../store/slices/productSlice";
import {
  CreateProductResponse,
  DeleteProductResponse,
  GetProductDetailRequest,
  GetProductDetailResponse,
  GetProductListRequest,
  GetProductListResponse,
  Product,
  ProductFormData,
  UpdateProductRequest,
  UpdateProductResponse,
  ListProductItemsResponse
} from "../../ProductMFE/Products/types/products";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import { toGraphQLBaseUnit } from "../../Utils/toGraphQLBaseUnit";
import { ProductItemStatus } from "../../ProductMFE/Products/enums/productItems";
import { isUserCompanyAdmin } from "./useProductItem";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

/** Matches `@kivunova/kivufrontendcommon` `graphql/graphqlAlertMarker.ts` (set on GraphQL client errors after `setAlert`). */
const KIVU_GRAPHQL_ALERT_SHOWN = "__KIVU_GRAPHQL_ALERT_SHOWN__";

function isGraphqlAlertAlreadyShown(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error as unknown as Record<string, unknown>)[KIVU_GRAPHQL_ALERT_SHOWN] ===
      true
  );
}

class ProductMutationRejectedError extends Error {
  constructor(public readonly responseCode: string) {
    super(responseCode);
    this.name = "ProductMutationRejectedError";
  }
}

/** Thrown after `setAlert` for unexpected createProduct response shapes. */
class CreateProductShapeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CreateProductShapeError";
  }
}

/**
 * useGetProductList
 * Fetch list of products
 */
export const useGetProductList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Product[], Error, GetProductListRequest>({
    mutationKey: ["getProductList"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching product list",
        buildLogContext(
          "products",
          "get-product-list",
          userId,
          companyId,
          storeId,
          {
            filters: JSON.stringify(input ?? {})
          }
        )
      );

      try {
        const response: GetProductListResponse = await fetchGraphQL(
          getProductListQuery,
          { input },
          setAlert
        );

        const {
          products,
          code,
          totalElements,
          totalPages,
          currentPage,
          pageSize
        } = response.listProducts;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch product list",
            buildLogContext(
              "products",
              "get-product-list",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        // ❌ Logically broken success response
        if (!products) {
          captureAppException(
            "API returned OK but products array missing",
            new Error("API returned OK but products array missing"),
            buildLogContext(
              "products",
              "get-product-list",
              userId,
              companyId,
              storeId
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        // ✅ Success: dispatch list and pagination from API response fields
        dispatch(setProductList(products));
        dispatch(
          setProductListPagination({
            totalElements: totalElements ?? products.length,
            currentPage: currentPage ?? 0,
            pageSize: pageSize ?? input?.size ?? 20,
            totalPages: totalPages ?? 1
          })
        );

        sendAppLog(
          LOG_LEVEL.INFO,
          "Product list fetched successfully",
          buildLogContext(
            "products",
            "get-product-list",
            userId,
            companyId,
            storeId,
            {
              productCount: products.length,
              totalElements: totalElements ?? 0,
              currentPage: currentPage ?? 0,
              totalPages: totalPages ?? 0
            }
          )
        );

        return products;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch product list",
          error,
          buildLogContext(
            "products",
            "get-product-list",
            userId,
            companyId,
            storeId,
            {
              filters: JSON.stringify(input ?? {})
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return [];
      }
    }
  });
};

/**
 * useGetProductDetail
 * Get product details by id
 */
export const useGetProductDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Product | null, Error, GetProductDetailRequest>({
    mutationKey: ["getProductDetail"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching product detail",
        buildLogContext(
          "products",
          "get-product-detail",
          userId,
          companyId,
          storeId,
          {
            productId: String(input.id)
          }
        )
      );

      try {
        const response: GetProductDetailResponse = await fetchGraphQL(
          getProductDetailQuery,
          { input },
          setAlert
        );

        const { product, code } = response.getProductDetail;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch product detail",
            buildLogContext(
              "products",
              "get-product-detail",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code),
                productId: String(input.id)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        // ❌ Logically broken success response
        if (!product) {
          captureAppException(
            "API returned OK but product object missing",
            new Error("API returned OK but product object missing"),
            buildLogContext(
              "products",
              "get-product-detail",
              userId,
              companyId,
              storeId,
              {
                productId: String(input.id)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ✅ Success
        dispatch(setProductDetail(product));

        sendAppLog(
          LOG_LEVEL.INFO,
          "Product detail fetched successfully",
          buildLogContext(
            "products",
            "get-product-detail",
            userId,
            companyId,
            storeId,
            {
              productId: String(product.id)
            }
          )
        );

        return product;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch product detail",
          error,
          buildLogContext(
            "products",
            "get-product-detail",
            userId,
            companyId,
            storeId,
            {
              productId: String(input.id)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return null;
      }
    }
  });
};

/**
 * useCreateProduct
 * Create a new product
 */
export const useCreateProduct = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<string | null, Error, ProductFormData>({
    mutationKey: ["createProduct"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating product",
        buildLogContext(
          "products",
          "create-product",
          userId,
          companyId,
          storeId,
          {
            productName: input.name
          }
        )
      );

      try {
        if (isUserCompanyAdmin(userDetails)) {
          const sid = input.storeId?.trim();
          if (!sid) {
            sendAppLog(
              LOG_LEVEL.WARNING,
              "Create product skipped: company admin missing storeId",
              buildLogContext(
                "products",
                "create-product",
                userId,
                companyId,
                storeId,
                {
                  productName: input.name
                }
              )
            );
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: ALERT_BANNER_CODE.STORE_ID_REQUIRED
            });
            throw new ProductMutationRejectedError("STORE_ID_REQUIRED");
          }
        }

        // Build API input: CreateProductInput uses 'category' (category ID), not 'categoryId'
        const apiInput: Record<string, unknown> = {
          name: input.name,
          sellingMode: input.sellingMode,
          category: String(input.categoryId ?? "").trim(),
          baseUnit: toGraphQLBaseUnit(input.baseUnit as string),
          isPartialSellingAllowed: input.isPartialSellingAllowed,
          brandName: String(input.brandName ?? "").trim(),
          ...(input.description?.trim() && {
            description: input.description.trim()
          }),
          ...(input.supplierId?.trim() && {
            supplierId: input.supplierId.trim()
          })
        };

        if (isUserCompanyAdmin(userDetails)) {
          const storeId = input.storeId?.trim();
          if (storeId) {
            apiInput.storeId = storeId;
          }
        }

        const response: CreateProductResponse = await fetchGraphQL(
          createProductQuery,
          { input: apiInput },
          setAlert
        );

        const createResult = response?.createProduct;

        // ❌ Unexpected response shape
        if (!createResult) {
          captureAppException(
            "Create product failed: missing createProduct",
            new Error("Create product failed: missing createProduct"),
            buildLogContext(
              "products",
              "create-product",
              userId,
              companyId,
              storeId,
              {
                productName: input.name
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          throw new CreateProductShapeError(
            "Create product failed: missing createProduct"
          );
        }

        // ⚠️ Expected API/business failure
        if (createResult.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Product creation rejected by API",
            buildLogContext(
              "products",
              "create-product",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(createResult.code),
                productName: input.name
              }
            )
          );

          const apiCode = String(createResult.code ?? "").trim();
          const alertCode =
            apiCode === ALERT_BANNER_CODE.STORE_ID_REQUIRED
              ? ALERT_BANNER_CODE.STORE_ID_REQUIRED
              : (createResult.code as unknown as ALERT_BANNER_CODE) ||
                ALERT_BANNER_CODE.INTERNAL_ERROR;

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: alertCode
          });
          throw new ProductMutationRejectedError(apiCode || "UNKNOWN");
        }

        const productId = createResult?.id;

        // ❌ Logically broken success response
        if (!productId) {
          captureAppException(
            "API returned OK but product ID missing",
            new Error("API returned OK but product ID missing"),
            buildLogContext(
              "products",
              "create-product",
              userId,
              companyId,
              storeId,
              {
                productName: input.name
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          throw new CreateProductShapeError(
            "API returned OK but product ID missing"
          );
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Product created successfully",
          buildLogContext(
            "products",
            "create-product",
            userId,
            companyId,
            storeId,
            {
              productId: String(productId),
              productName: input.name
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        dispatch(incrementProductListRefreshTrigger());

        return productId;
      } catch (error) {
        if (error instanceof ProductMutationRejectedError) {
          throw error;
        }
        if (error instanceof CreateProductShapeError) {
          throw error;
        }
        if (isGraphqlAlertAlreadyShown(error)) {
          throw error;
        }

        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to create product",
          error,
          buildLogContext(
            "products",
            "create-product",
            userId,
            companyId,
            storeId,
            {
              productName: input.name
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw error instanceof Error ? error : new Error(String(error));
      }
    }
  });
};

/**
 * useUpdateProduct
 * Update an existing product
 */
export const useUpdateProduct = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<string | null, Error, UpdateProductRequest>({
    mutationKey: ["updateProduct"],

    mutationFn: async (request) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      const input = request.input;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating product",
        buildLogContext(
          "products",
          "update-product",
          userId,
          companyId,
          storeId,
          {
            productId: String(input.id)
          }
        )
      );

      try {
        const graphqlInput = {
          ...input,
          ...(input.baseUnit !== undefined &&
          input.baseUnit !== null &&
          String(input.baseUnit).trim() !== ""
            ? { baseUnit: toGraphQLBaseUnit(input.baseUnit) }
            : {})
        };

        const response: UpdateProductResponse = await fetchGraphQL(
          updateProductQuery,
          { input: graphqlInput },
          setAlert
        );

        const updateResult = response?.updateProduct;

        // ❌ Unexpected response shape
        if (!updateResult) {
          captureAppException(
            "Update product failed: missing updateProduct",
            new Error("Update product failed: missing updateProduct"),
            buildLogContext(
              "products",
              "update-product",
              userId,
              companyId,
              storeId,
              {
                productId: String(input.id)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ⚠️ Expected API/business failure
        if (updateResult.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Product update rejected by API",
            buildLogContext(
              "products",
              "update-product",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(updateResult.code),
                productId: String(input.id)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: updateResult.code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        const productId = updateResult?.id;

        // ❌ Logically broken success response
        if (!productId) {
          captureAppException(
            "API returned OK but product ID missing",
            new Error("API returned OK but product ID missing"),
            buildLogContext(
              "products",
              "update-product",
              userId,
              companyId,
              storeId,
              {
                productId: String(input.id)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Product updated successfully",
          buildLogContext(
            "products",
            "update-product",
            userId,
            companyId,
            storeId,
            {
              productId: String(productId)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        return productId;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to update product",
          error,
          buildLogContext(
            "products",
            "update-product",
            userId,
            companyId,
            storeId,
            {
              productId: String(input.id)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return null;
      }
    },

    onSuccess: async (productId) => {
      if (!productId) return;

      try {
        const response: GetProductDetailResponse = await fetchGraphQL(
          getProductDetailQuery,
          { input: { id: productId } },
          setAlert
        );
        const { product, code } = response?.getProductDetail ?? {};
        if (code === API_RESPONSE_CODE.OK && product) {
          dispatch(setProductDetail(product));
        }
      } catch {
        // Refetch failed; update already succeeded — don't surface an error
      }
    }
  });
};

/**
 * useDeleteProduct
 * Delete an existing product
 */
export const useDeleteProduct = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<boolean, Error, { id: string }>({
    mutationKey: ["deleteProduct"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started deleting product",
        buildLogContext(
          "products",
          "delete-product",
          userId,
          companyId,
          storeId,
          {
            productId: String(input.id)
          }
        )
      );

      try {
        const response: DeleteProductResponse = await fetchGraphQL(
          deleteProductQuery,
          { input },
          setAlert
        );

        const deleteResult = response?.deleteProduct;

        // ❌ Unexpected response shape
        if (!deleteResult) {
          captureAppException(
            "Delete product failed: missing deleteProduct",
            new Error("Delete product failed: missing deleteProduct"),
            buildLogContext(
              "products",
              "delete-product",
              userId,
              companyId,
              storeId,
              {
                productId: String(input.id)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return false;
        }

        // ⚠️ Expected API/business failure
        if (deleteResult.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Product deletion rejected by API",
            buildLogContext(
              "products",
              "delete-product",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(deleteResult.code),
                productId: String(input.id)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: deleteResult.code as unknown as ALERT_BANNER_CODE
          });
          return false;
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Product deleted successfully",
          buildLogContext(
            "products",
            "delete-product",
            userId,
            companyId,
            storeId,
            {
              productId: String(input.id)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        return true;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to delete product",
          error,
          buildLogContext(
            "products",
            "delete-product",
            userId,
            companyId,
            storeId,
            {
              productId: String(input.id)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return false;
      }
    }
  });
};

/**
 * useHasActiveProductItems
 * Returns true if the product has any items in CREATED, IN_STOCK, or RESERVED state
 */
export const useHasActiveProductItems = (productId: string | undefined) => {
  const { setAlert } = useContext(AlertBannerContext);
  const [hasActive, setHasActive] = useState(false);
  const [loading, setLoading] = useState(!!productId);

  const ACTIVE_STATES: ProductItemStatus[] = [
    ProductItemStatus.CREATED,
    ProductItemStatus.IN_STOCK,
    ProductItemStatus.RESERVED
  ];

  useEffect(() => {
    if (!productId) {
      setHasActive(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setHasActive(false);
    setLoading(true);

    const check = async () => {
      try {
        const PAGE_SIZE = 100;
        let page = 0;
        let totalElements = 0;
        let fetched = 0;

        do {
          const response: ListProductItemsResponse = await fetchGraphQL(
            listProductItemsByProductQuery,
            {
              input: {
                productId,
                pageSize: PAGE_SIZE,
                page
              }
            },
            setAlert
          );

          if (cancelled) return;

          const result = response?.listProductItemsByProduct;

          // Treat a non-OK response code as a failure to keep the fail-safe guarantee
          if (result?.code !== API_RESPONSE_CODE.OK) {
            setHasActive(true);
            return;
          }

          const items = result?.items ?? [];
          totalElements = result?.totalElements ?? 0;
          fetched += items.length;

          // Found an active item — no need to paginate further
          if (items.some((item) => ACTIVE_STATES.includes(item.state))) {
            setHasActive(true);
            return;
          }

          // Safety: if API returns fewer items than expected, stop to avoid infinite loop
          if (items.length < PAGE_SIZE) break;

          page++;
        } while (fetched < totalElements);

        // All pages checked — no active items found
        if (!cancelled) setHasActive(false);
      } catch {
        // Fail safe — block deletion if check fails
        if (!cancelled) setHasActive(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [productId]);

  return { hasActive, loading };
};

/**
 * useGetLowStockAlerts
 * Fetch low stock alerts
 */
export const useGetLowStockAlerts = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Product[], Error, void>({
    mutationKey: ["getLowStockAlerts"],

    mutationFn: async () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching low stock alerts",
        buildLogContext(
          "products",
          "get-low-stock-alerts",
          userId,
          companyId,
          storeId
        )
      );

      try {
        const response: any = await fetchGraphQL(
          getLowStocAlertsQuery,
          {},
          setAlert
        );

        const { products, code } = response.getLowStockAlerts;

        if (code !== API_RESPONSE_CODE.OK) {
          // Gracefully handle PARSING_ERROR (likely missing backend resolver) without showing alert
          if (code === "PARSING_ERROR") {
            return [];
          }

          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch low stock alerts",
            buildLogContext(
              "products",
              "get-low-stock-alerts",
              userId,
              companyId,
              storeId,
              { apiCode: String(code) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        const alerts = products || [];
        dispatch(setLowStockAlerts(alerts));
        return alerts;
      } catch (error) {
        captureAppException(
          "Failed to fetch low stock alerts",
          error,
          buildLogContext(
            "products",
            "get-low-stock-alerts",
            userId,
            companyId,
            storeId
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return [];
      }
    }
  });
};
